/**
 * ArchivedAccountsPage — iter-259 (Feb 2026)
 * ==========================================
 * Generic archived-accounts list. Two variants share this component
 * via the `variant` prop:
 *   • variant="merchant" → /control/archive/cloudgm-business/accounts
 *                          hits /api/admin/merchant-users/*
 *   • variant="cgc"      → /control/archive/cloudgm/accounts
 *                          hits /api/admin/cgc-users/*
 *
 * Each row lets the operator:
 *   • Restore — moves the account out of the archive (unblock is then
 *     available on the source account admin page).
 *   • Delete  — cascades on the source platform (still requires
 *     blocked+archived — enforced on backend).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Archive, Loader2, RotateCcw, Trash2, Mail, Phone, RefreshCw,
  AlertTriangle, User as UserIcon, Search, ShieldCheck,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VARIANTS = {
  merchant: {
    titleAr: 'الحسابات المؤرشفة — منصة التجار',
    titleEn: 'Archived Accounts — Merchant Platform',
    breadcrumbParent: { labelAr: 'CLOUDGM Business', labelEn: 'CLOUDGM Business', path: '/control/archive/cloudgm-business' },
    apiPrefix: '/admin/merchant-users',
    detailRoute: (uid, email) => `/manage-platform/merchant-users/${uid}?email=${encodeURIComponent(email || '')}`,
    color: '#8B5CF6',
    testid: 'merchant-archived-accounts',
  },
  cgc: {
    titleAr: 'الحسابات المؤرشفة — منصة العملاء',
    titleEn: 'Archived Accounts — Customer Platform',
    breadcrumbParent: { labelAr: 'CLOUDGM', labelEn: 'CLOUDGM', path: '/control/archive/cloudgm' },
    apiPrefix: '/admin/cgc-users',
    detailRoute: (uid, email) => `/manage-platform/cgc-users/${uid}?email=${encodeURIComponent(email || '')}`,
    color: '#1E9BD9',
    testid: 'cgc-archived-accounts',
  },
};

export default function ArchivedAccountsPage({ variant }) {
  const cfg = VARIANTS[variant];
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}${cfg.apiPrefix}/archived`, { headers: getAuthHeaders() });
      setRows(r.data?.data || []);
    } catch (err) {
      toast.error(t('فشل تحميل الأرشيف', 'Failed to load archive'));
    } finally { setLoading(false); }
  }, [cfg.apiPrefix, t]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => (
      (r.email || '').toLowerCase().includes(s) ||
      (r.name || '').toLowerCase().includes(s) ||
      (r.user_id || '').toLowerCase().includes(s)
    ));
  }, [q, rows]);

  const restore = async (row) => {
    if (!window.confirm(t(
      `استعادة ${row.email} من الأرشيف؟`,
      `Restore ${row.email} from archive?`,
    ))) return;
    setBusy(`restore-${row.user_id}`);
    try {
      await axios.post(`${API}${cfg.apiPrefix}/${row.user_id}/restore`, {},
                        { headers: getAuthHeaders() });
      toast.success(t('تمت الاستعادة', 'Restored'));
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(''); }
  };

  const del = async (row) => {
    if (!window.confirm(t(
      `⚠️ حذف ${row.email} نهائياً — لا يمكن التراجع. تأكيد؟`,
      `⚠️ Permanently delete ${row.email} — CANNOT be undone. Continue?`,
    ))) return;
    setBusy(`delete-${row.user_id}`);
    try {
      await axios.delete(`${API}${cfg.apiPrefix}/${row.user_id}/cascade`,
                          { headers: getAuthHeaders() });
      toast.success(t('تم الحذف نهائياً', 'Deleted permanently'));
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(''); }
  };

  return (
    <DashboardLayout
      title={cfg.titleAr}
      titleEn={cfg.titleEn}
      subtitle={isAr ? 'استعد الحساب من الأرشيف أو احذفه نهائياً' : 'Restore or permanently delete an archived account'}
      screenCode="ARC-ACCT"
      backPath={cfg.breadcrumbParent.path}
      onBack={() => navigate(cfg.breadcrumbParent.path)}
      breadcrumb={[
        { labelAr: 'الأرشيف', labelEn: 'Archive', path: '/control/archive' },
        { labelAr: cfg.breadcrumbParent.labelAr, labelEn: cfg.breadcrumbParent.labelEn, path: cfg.breadcrumbParent.path },
        { labelAr: 'الحسابات المؤرشفة', labelEn: 'Archived Accounts' },
      ]}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid={cfg.testid}>
        {/* Search + refresh */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30 pointer-events-none" />
            <Input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t('ابحث بالبريد/الاسم…', 'Search by email/name…')}
              className="bg-[#1a1a2e] border-white/10 text-white text-sm pe-9"
              data-testid={`${cfg.testid}-search`}
              dir={isAr ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-white/50">
              {t('العدد:', 'Count:')} <span className="text-white font-semibold">{filtered.length}</span>
            </span>
            <Button size="sm" variant="outline" onClick={load}
                    className="border-white/10 text-white/70 hover:bg-white/5"
                    data-testid={`${cfg.testid}-refresh`}>
              <RefreshCw className="h-3 w-3 me-1.5" />
              {t('تحديث', 'Refresh')}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[0,1,2].map((i) => (
              <div key={i} className="h-24 bg-white/[0.02] rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/50 bg-white/[0.02] rounded-2xl border border-white/10">
            <Archive className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p>{q
              ? t(`لا توجد نتائج لـ "${q}"`, `No results for "${q}"`)
              : t('لا توجد حسابات مؤرشفة', 'No archived accounts')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((row, idx) => (
              <motion.div
                key={row.user_id || idx}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="p-4 bg-white/[0.02] rounded-2xl border border-white/10 hover:border-amber-500/30 transition-colors"
                data-testid={`${cfg.testid}-row-${row.user_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                      <UserIcon className="h-5 w-5 text-amber-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-semibold truncate">{row.name || '—'}</p>
                      <p className="text-[12px] text-white/60 flex items-center gap-1 truncate">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span dir="ltr" className="truncate">{row.email}</span>
                      </p>
                      {row.phone && (
                        <p className="text-[11px] text-white/45 flex items-center gap-1">
                          <Phone className="h-2.5 w-2.5 shrink-0" />
                          <span dir="ltr">{row.phone}</span>
                        </p>
                      )}
                      {row.ban_reason && (
                        <p className="text-[10px] text-red-300/80 mt-0.5 truncate">
                          {t('سبب الحظر:', 'Ban reason:')} "{row.ban_reason}"
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-white/40">
                      {t('أُرشف:', 'Archived:')} {fmtDate(row.archived_at)}
                    </span>
                    <Button size="sm"
                            onClick={() => navigate(cfg.detailRoute(row.user_id, row.email))}
                            className="bg-[#06b6d4]/15 hover:bg-[#06b6d4]/25 text-[#06b6d4] border border-[#06b6d4]/30"
                            data-testid={`${cfg.testid}-view-${row.user_id}`}>
                      <ShieldCheck className="h-3 w-3 me-1" />
                      {t('إدارة الحساب', 'Manage Account')}
                    </Button>
                    <Button size="sm"
                            onClick={() => restore(row)}
                            disabled={busy === `restore-${row.user_id}`}
                            className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-500/40"
                            data-testid={`${cfg.testid}-restore-${row.user_id}`}>
                      {busy === `restore-${row.user_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3 me-1" />}
                      {t('استعادة من الأرشيف', 'Restore')}
                    </Button>
                    <Button size="sm"
                            onClick={() => del(row)}
                            disabled={busy === `delete-${row.user_id}`}
                            className="bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/40"
                            data-testid={`${cfg.testid}-delete-${row.user_id}`}>
                      {busy === `delete-${row.user_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 me-1" />}
                      {t('حذف نهائي', 'Delete')}
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-6 p-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.05] flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-[12px] text-amber-200/90">
            {t(
              'ملاحظة: الحذف النهائي يُمرَّر إلى منصة المصدر ولا يمكن التراجع عنه. لا يمكن فك الحظر أو الحذف عن حساب لا يزال في الأرشيف — استعده أولاً.',
              'Cascade delete is forwarded to the source platform and CANNOT be undone. Unblock is disabled on archived accounts — restore first.',
            )}
          </div>
        </div>
      </section>
    </DashboardLayout>
  );
}

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
